import { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import {IconButton, Box, Typography} from "@mui/material";
import CustomButton from "../../../../components/CustomButton";
import {useTranslation} from "react-i18next";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import {
  getGoogleMapsPinIconForStatus,
  getStatusBadgeBackgroundColor,
} from "../../../../constants/statusPalette";

const defaultCenter = { lat: 37.0902, lng: -95.7129 };
const mapContainerStyle = { width: "100%", height: "100%" };


const WorkerPins = ({ statusFilter, page, limit, selectedWorkerId, onClearFilter, companyId }) => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredWorker, setHoveredWorker] = useState(null);
const {t}=useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const mapRef = useRef(null);
  const fullMapRef = useRef(null);

  const statusMap = {
  invited: "statuses.invited",
  active: "statuses.active",
  inactive: "statuses.inactive",
  pending: "statuses.pending",
};

const statusCounts = workers.reduce(
  (acc, w) => {
    const status = w.status?.toLowerCase();
    if (status && acc[status] !== undefined) {
      acc[status] += 1;
    }
    return acc;
  },
  {
    invited: 0,
    pending: 0,
    active: 0,
    inactive: 0,
  }
);


useEffect(() => {
  if (!decryptedToken) return;

  setLoading(true);

  const params = new URLSearchParams({
    page: page,
    limit: limit,
  });

  if (statusFilter) {
    params.set("status", statusFilter);
  }

  if (companyId) {
    params.set("company_id", String(companyId));
  }

  const url = `${BASE_URL}/company-admins/workers?${params.toString()}`;

  fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${decryptedToken}`,
      "Content-Type": "application/json",
    },
  })
    .then(async (res) => {
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.error) {
        throw new Error(data?.message || "Failed to fetch pins");
      }
      return data;
    })
    .then((data) => {
      let list = data?.data?.workers || [];
      // Filter by latitude and longitude
      const filtered = list.filter((w) => w.latitude && w.longitude);
      // If selectedWorkerId is provided, show only that worker
      const finalList = selectedWorkerId
        ? filtered.filter((w) => w.id === selectedWorkerId)
        : filtered;
      setWorkers(finalList);
    })
    .catch((err) => {
      console.error("Error fetching pins:", err);
      setWorkers([]);
    })
    .finally(() => setLoading(false));
}, [page, limit, statusFilter, decryptedToken, selectedWorkerId]);



const [showFullMap, setShowFullMap] = useState(false);

  const translatedStatus =
    statusFilter && statusMap[statusFilter]
      ? t(statusMap[statusFilter])
      : t("statuses.all");
const statusTranslate = {
  active: t("statuses.active"),
  inactive: t("statuses.inactive"),
  pending: t("statuses.pending"),
  invited: t("statuses.invited"),
};
/**
 * Pin fill must match the inactive badge in the workers table (`WorkerStatusDropdown`),
 * which uses the same keys as `getStatusBadgeBackgroundColor` / `statusPalette`.
 */
const getMarkerIcon = (status) => getGoogleMapsPinIconForStatus(status);

const getMapCenter = () => {
  if (!workers.length) return defaultCenter;

  return {
    lat: parseFloat(workers[0].latitude),
    lng: parseFloat(workers[0].longitude),
  };
};

const fitMapToWorkers = (map) => {
  if (!map || !window.google?.maps) return;

  if (!workers.length) {
    map.setCenter(defaultCenter);
    map.setZoom(5);
    return;
  }

  if (workers.length === 1) {
    map.setCenter({
      lat: parseFloat(workers[0].latitude),
      lng: parseFloat(workers[0].longitude),
    });
    map.setZoom(13);
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  workers.forEach((worker) => {
    bounds.extend({
      lat: parseFloat(worker.latitude),
      lng: parseFloat(worker.longitude),
    });
  });
  map.fitBounds(bounds, 50);
};

useEffect(() => {
  if (hoveredWorker) {
    const stillVisible = workers.some((worker) => worker.id === hoveredWorker.id);
    if (!stillVisible) setHoveredWorker(null);
  }
}, [workers, hoveredWorker]);

useEffect(() => {
  fitMapToWorkers(mapRef.current);
  if (showFullMap) {
    fitMapToWorkers(fullMapRef.current);
  }
}, [workers, showFullMap]);

  return (
    <div style={{ height: "50vh", width: "100%", position: "relative" ,borderRadius:"12px",marginBottom:"30px"}}>
      {/* Loader overlay */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            backgroundColor: "rgba(255,255,255,0.7)",
            borderRadius: "10px",
            padding: "20px",
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}

      {/* Info Text */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#666",
            fontFamily: "Poppins, sans-serif",
            fontStyle: "italic",
          }}
        >
          {t("workers.workerPins.locationInfo")}
        </Typography>
      </Box>

<Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",mb:1}}>

    {/* Status Legend */}
    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
      
      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{
          width: 12, height: 12, borderRadius: "50%", background: getStatusBadgeBackgroundColor("invited"),
        }}></span>
       {/* {t("statuses.invited")} */}
           {t("statuses.invited")} ({statusCounts.invited})
      </span>

      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{
          width: 12, height: 12, borderRadius: "50%", background: getStatusBadgeBackgroundColor("pending"),
        }}></span>
     {/* {t("statuses.pending")} */}
         {t("statuses.pending")} ({statusCounts.pending})

      </span>

      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{
          width: 12, height: 12, borderRadius: "50%", background: getStatusBadgeBackgroundColor("active"),
        }}></span>
        {/* {t("statuses.active")} */}
            {t("statuses.active")} ({statusCounts.active})

      </span>

      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{
          width: 12, height: 12, borderRadius: "50%", background: getStatusBadgeBackgroundColor("inactive"),
        }}></span>
     {/* {t("statuses.inactive")} */}
         {t("statuses.inactive")} ({statusCounts.inactive})

      </span>

    </div>

    {/* Clear filter button when worker is selected */}
    {selectedWorkerId && onClearFilter && (
      <IconButton
        onClick={onClearFilter}
        sx={{
          backgroundColor: '#f44336',
          color: 'white',
          '&:hover': { backgroundColor: '#d32f2f' },
          padding: '4px',
          width: 32,
          height: 32
        }}
        title="Clear filter"
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    )}
</Box>


      <Box sx={{ position: "relative", width: "100%", height: "100%", borderRadius: "12px" }}>
  {window.google?.maps ? (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={getMapCenter()}
      zoom={5}
      onLoad={(map) => {
        mapRef.current = map;
        fitMapToWorkers(map);
      }}
      onUnmount={() => {
        mapRef.current = null;
      }}
      options={{
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
      }}
    >
      {workers.map((worker) => (
        <Marker
          key={worker.id}
          position={{
            lat: parseFloat(worker.latitude),
            lng: parseFloat(worker.longitude),
          }}
          icon={getMarkerIcon(worker.status)}
          onMouseOver={() => setHoveredWorker(worker)}
        />
      ))}

      {hoveredWorker && (
        <InfoWindow
          position={{
            lat: parseFloat(hoveredWorker.latitude),
            lng: parseFloat(hoveredWorker.longitude),
          }}
          options={{ disableAutoPan: true }}
          onCloseClick={() => setHoveredWorker(null)}
        >
          <div style={{ fontFamily: "Poppins, sans-serif", minWidth: "160px" }}>
            <p style={{ margin: 0, fontWeight: 600 }}>
              {hoveredWorker.first_name} {hoveredWorker.last_name}
            </p>
            <p style={{ margin: 0, fontSize: 12 }}>{hoveredWorker.email}</p>
            <p style={{ margin: 0, fontSize: 12 }}>
              {statusTranslate[hoveredWorker.status?.toLowerCase()] || hoveredWorker.status}
            </p>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "12px",
        backgroundColor: "#f5f5f5",
      }}
    >
      <CircularProgress color="primary" />
    </Box>
  )}

  {/* ✅ Full Map Button – EXACT UI STYLE */}
  <CustomButton
    onClick={() => setShowFullMap(true)}
    label={
      <>
        {t("fullMap")}
        <Box
          component="img"
         src={maparrow}  // replace with your icon
          alt="view icon"
          sx={{ ml: 1, width: 15, height: 13 }}
        />
      </>
    }
    size="small"
    sx={{
      position: "absolute",
      top: 12,
      right: 12,
      py: 0.5,
      height: 28,
      zIndex: 1000,
      fontSize: "12px",
      borderRadius: "6px",
    }}
  />
</Box>

      {showFullMap && (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      backgroundColor: "#fff",
      zIndex: 2000,
    }}
  >
    {/* Close icon */}
    {/* <Box
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        zIndex: 2100,
        cursor: "pointer",
        fontSize: "28px",
        fontWeight: 600,
      }}
      onClick={() => setShowFullMap(false)}
    >
      ✕
    </Box> */}
       <IconButton
          onClick={() => setShowFullMap(false)}
          sx={{ position: "absolute", top: 16, right: 16, zIndex: 2100,backgroundColor:"white",
            "&:hover": { backgroundColor: "white" }
           }}
        >
         <CloseIcon sx={{color:"red"}} />
        </IconButton>

    {window.google?.maps ? (
      <GoogleMap
        mapContainerStyle={{ width: "100vw", height: "100vh" }}
        center={getMapCenter()}
        zoom={5}
        onLoad={(map) => {
          fullMapRef.current = map;
          fitMapToWorkers(map);
        }}
        onUnmount={() => {
          fullMapRef.current = null;
        }}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
        }}
      >
        {workers.map((worker) => (
          <Marker
            key={worker.id}
            position={{
              lat: parseFloat(worker.latitude),
              lng: parseFloat(worker.longitude),
            }}
            icon={getMarkerIcon(worker.status)}
            onMouseOver={() => setHoveredWorker(worker)}
          />
        ))}

        {hoveredWorker && (
          <InfoWindow
            position={{
              lat: parseFloat(hoveredWorker.latitude),
              lng: parseFloat(hoveredWorker.longitude),
            }}
            options={{ disableAutoPan: true }}
            onCloseClick={() => setHoveredWorker(null)}
          >
            <div style={{ fontFamily: "Poppins, sans-serif", minWidth: "160px" }}>
              <p style={{ margin: 0, fontWeight: 600 }}>
                {hoveredWorker.first_name} {hoveredWorker.last_name}
              </p>
              <p style={{ margin: 0, fontSize: 12 }}>{hoveredWorker.email}</p>
              <p style={{ margin: 0, fontSize: 12 }}>
                {statusTranslate[hoveredWorker.status?.toLowerCase()] || hoveredWorker.status}
              </p>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    ) : (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    )}
  </Box>
)}

{statusFilter && (
  <div
    style={{
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: "16px",
      fontFamily: "Poppins, sans-serif",
      marginBottom: "10px",
    }}
  >
    {/* Applied status text */}
    <span>
      
      {t("showingWorkersStatus")}{" "}
      <span style={{ textTransform: "capitalize" }}>
        {translatedStatus}
      </span>
    </span>



  </div>
)}


    </div>
  );
};

export default WorkerPins;





